import { useEffect, useRef, ReactNode } from 'react';

interface TiltCardProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  intensity?: number;
}

export default function TiltCard({ children, className = '', style = {}, intensity = 10 }: TiltCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const frameRef = useRef<number | undefined>(undefined);
  const activeRef = useRef(false);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(pointer: fine)');
    activeRef.current = finePointer.matches && !reduceMotion.matches;

    const onPreferenceChange = () => {
      activeRef.current = finePointer.matches && !reduceMotion.matches;
      if (!activeRef.current) {
        card.style.transform = '';
        card.style.transition = '';
      }
    };

    reduceMotion.addEventListener?.('change', onPreferenceChange);
    finePointer.addEventListener?.('change', onPreferenceChange);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      reduceMotion.removeEventListener?.('change', onPreferenceChange);
      finePointer.removeEventListener?.('change', onPreferenceChange);
    };
  }, []);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      card.classList.add('visible');
      return;
    }

    const revealObserver = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          card.classList.add('visible');
          revealObserver.unobserve(card);
        }
      }),
      { threshold: 0.1, rootMargin: '0px 0px -8%' }
    );

    revealObserver.observe(card);
    return () => revealObserver.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card || !activeRef.current) return;

    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      if (!rectRef.current) rectRef.current = card.getBoundingClientRect();
      const rect = rectRef.current;
      if (!rect || rect.width === 0 || rect.height === 0) return;
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      card.style.transform = `perspective(1100px) rotateY(${x * intensity}deg) rotateX(${-y * intensity}deg) translateZ(5px)`;
      card.style.setProperty('--tilt-x', `${x * 24}px`);
      card.style.setProperty('--tilt-y', `${y * 24}px`);
    });
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card || !activeRef.current) return;
    rectRef.current = null;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    card.style.transform = 'perspective(1100px) rotateY(0deg) rotateX(0deg) translateZ(0px)';
    card.style.setProperty('--tilt-x', '0px');
    card.style.setProperty('--tilt-y', '0px');
    card.style.transition = 'transform 0.5s cubic-bezier(0.16,1,0.3,1)';
    window.setTimeout(() => { if (card) card.style.transition = ''; }, 500);
  };

  const handleMouseEnter = () => {
    const card = cardRef.current;
    if (card && activeRef.current) {
      card.style.transition = 'transform 0.1s ease';
      rectRef.current = card.getBoundingClientRect();
    }
  };

  return (
    <div
      ref={cardRef}
      className={`tilt-card scroll-fade-card ${className}`}
      style={{ ...style, transformStyle: 'preserve-3d', willChange: 'transform' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      {children}
    </div>
  );
}
